import { useEffect, useState } from 'react';
import Modal from '../components/common/Modal';
import { userApi } from '../api/services';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { UserPlus, Edit2, Key, Power, Loader2, CheckCircle2, XCircle } from 'lucide-react';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([userApi.list(), userApi.roles()]);
      setUsers(Array.isArray(u.data) ? u.data : (u.data.users || []));
      setRoles(Array.isArray(r.data) ? r.data : (r.data.roles || []));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load users');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(u) {
    try {
      if (u.is_active) await userApi.deactivate(u.id);
      else await userApi.reactivate(u.id);
      toast.success(u.is_active ? 'Deactivated' : 'Reactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  }

  const shown = users.filter((u) => showInactive || u.is_active);

  return (
    <div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show deactivated
          </label>
          <button onClick={() => setCreateOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            <UserPlus className="w-4 h-4" /> New user
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : shown.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">No users.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Roles</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Last login</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{u.first_name} {u.last_name}</td>
                    <td className="px-4 py-2 text-gray-700">{u.email}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).filter(Boolean).map((r) => (
                          <span key={r} className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{r}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <XCircle className="w-3.5 h-3.5" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {u.last_login_at ? format(new Date(u.last_login_at), 'dd MMM yyyy HH:mm') : 'never'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <IconBtn onClick={() => setEditUser(u)} title="Edit"><Edit2 className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn onClick={() => setResetUser(u)} title="Reset password"><Key className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn onClick={() => toggleActive(u)} title={u.is_active ? 'Deactivate' : 'Reactivate'}>
                          <Power className={`w-3.5 h-3.5 ${u.is_active ? 'text-red-600' : 'text-green-600'}`} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {createOpen && (
        <UserFormModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onDone={load}
                       roles={roles} />
      )}
      {editUser && (
        <UserFormModal isOpen={!!editUser} onClose={() => setEditUser(null)} onDone={load}
                       roles={roles} user={editUser} />
      )}
      {resetUser && (
        <ResetPasswordModal isOpen={!!resetUser} onClose={() => setResetUser(null)}
                            user={resetUser} />
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title }) {
  return (
    <button onClick={onClick} title={title}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900">
      {children}
    </button>
  );
}

function UserFormModal({ isOpen, onClose, onDone, roles, user }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email:     user?.email || '',
    password:  '',
    firstName: user?.first_name || '',
    lastName:  user?.last_name || '',
    phone:     user?.phone || '',
    isActive:  user?.is_active ?? true,
    roles:     user?.roles ? user.roles.filter(Boolean) : [],
  });
  const [saving, setSaving] = useState(false);

  function toggleRole(name) {
    const next = form.roles.includes(name)
      ? form.roles.filter((r) => r !== name)
      : [...form.roles, name];
    setForm({ ...form, roles: next });
  }

  async function save() {
    if (!form.email.trim() || !form.firstName.trim() || !form.lastName.trim()) {
      toast.error('Email, first name and last name are required'); return;
    }
    if (!isEdit && form.password.length < 8) { toast.error('Password must be 8+ chars'); return; }
    if (form.roles.length === 0) { toast.error('Pick at least one role'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await userApi.update(user.id, {
          firstName: form.firstName, lastName: form.lastName,
          phone: form.phone || null, isActive: form.isActive, roles: form.roles,
        });
        toast.success('User updated');
      } else {
        await userApi.create({
          email: form.email, password: form.password,
          firstName: form.firstName, lastName: form.lastName,
          phone: form.phone || null, roles: form.roles,
        });
        toast.success('User created');
      }
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit user' : 'New user'}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">First name *</span>
            <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Last name *</span>
            <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Email *</span>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 disabled={isEdit} type="email"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
        </label>
        {!isEdit && (
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Initial password *</span>
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                   type="password"
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
        )}
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Phone</span>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div>
          <div className="text-xs text-gray-600 mb-1">Roles *</div>
          <div className="flex flex-wrap gap-2">
            {roles.map((r) => (
              <label key={r.id} className={`cursor-pointer px-3 py-1.5 rounded-lg text-sm border ${
                form.roles.includes(r.name) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-200 text-gray-700'
              }`}>
                <input type="checkbox" className="mr-1" checked={form.roles.includes(r.name)}
                       onChange={() => toggleRole(r.name)} />
                {r.display_name || r.name}
              </label>
            ))}
          </div>
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isActive}
                   onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            Active
          </label>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ isOpen, onClose, user }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (password.length < 8) { toast.error('Password must be 8+ chars'); return; }
    setSaving(true);
    try {
      await userApi.resetPassword(user.id, { password });
      toast.success('Password reset');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Reset password — ${user.email}`}>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">New password *</span>
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <span className="block text-[11px] text-gray-500 mt-1">Share this via a secure channel. User won't be notified automatically.</span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Reset
          </button>
        </div>
      </div>
    </Modal>
  );
}
